import { create } from "zustand";
import { api, type Presence, type Follow, type Pin, type Saved, type Scheduled, type Boot, type FileRef, type Message, type Room, type RoomState, type SyncResult, type Task, type TaskEvent, type User } from "./api";

export type Route =
  | { v: "home" }
  | { v: "room"; id: number; thread?: number; focus?: number }
  | { v: "tasks"; tab: "everyone" | "mine" }
  | { v: "task"; id: number }
  | { v: "search"; q: string }
  | { v: "mentions" }
  | { v: "threads" }
  | { v: "later" }
  | { v: "unreads" };

export interface Pending {
  clientId: string; roomId: number; body: string; parentId?: number; alsoInChat?: boolean;
  files: FileRef[]; createdAt: string; failed?: string;
}

export interface State {
  phase: "loading" | "signed-out" | "ready";
  online: boolean;
  me: User | null;
  users: Record<number, User>;
  rooms: Record<number, Room>;
  states: Record<number, RoomState>;
  tasks: Record<number, Task>;
  msgs: Record<number, Message[]>;
  older: Record<number, boolean>;
  byId: Record<number, Message>;
  threads: Record<number, Message[]>;
  events: Record<number, TaskEvent[]>;
  sources: Record<number, Message | null>;
  pending: Pending[];
  neoUrl: string | null;
  serverTime: string | null;
  route: Route;
  presence: Presence;
  follows: Record<number, Follow>;
  saved: Record<number, Saved>;
  pins: Pin[];
  scheduled: Scheduled[];
  mentionsSeenId: number;
  /** "roomId:threadId" → who is typing and until when. */
  typing: Record<string, Record<number, number>>;
  /** Your message being edited (↑ in an empty box). */
  editingId: number | null;
  /** Chats you marked unread: don't mark them read again until you leave them. */
  holdUnread: Record<number, true>;
}

export const useStore = create<State>(() => ({
  phase: "loading", online: false, me: null, users: {}, rooms: {}, states: {}, tasks: {}, msgs: {}, older: {}, byId: {},
  threads: {}, events: {}, sources: {}, pending: [], neoUrl: null, serverTime: null, route: { v: "home" }, presence: {}, follows: {}, saved: {}, pins: [], scheduled: [], mentionsSeenId: 0, typing: {}, editingId: null, holdUnread: {},
}));

const set = useStore.setState;
const get = useStore.getState;
const byKey = <T extends { id: number }>(l: T[]) => Object.fromEntries(l.map((x) => [x.id, x])) as Record<number, T>;
const insertSorted = (list: Message[], m: Message) => {
  const i = list.findIndex((x) => x.id === m.id);
  if (i >= 0) { const c = list.slice(); c[i] = m; return c; }
  const c = [...list, m];
  if (c.length > 1 && c[c.length - 2].id > m.id) c.sort((a, b) => a.id - b.id);
  return c;
};
export const inChat = (m: Message) => m.parentId == null || m.alsoInChat;

export function applyBoot(b: Boot) {
  set((s) => ({
    phase: "ready", me: b.me, users: byKey(b.users), rooms: byKey(b.rooms),
    states: Object.fromEntries(b.states.map((x) => [x.roomId, x])), tasks: byKey(b.tasks), neoUrl: b.neoUrl,
    follows: Object.fromEntries(b.follows.map((f) => [f.rootId, f])), saved: Object.fromEntries(b.saved.map((x) => [x.messageId, x])),
    pins: b.pins, scheduled: b.scheduled, mentionsSeenId: b.mentionsSeenId,
    serverTime: s.serverTime && s.serverTime < b.serverTime ? s.serverTime : b.serverTime,
    // Drop cached chats the person no longer belongs to.
    msgs: Object.fromEntries(Object.entries(s.msgs).filter(([id]) => b.rooms.some((r) => r.id === Number(id)))),
  }));
}

export function applySync(r: SyncResult) {
  set((s) => ({
    users: byKey(r.users), rooms: byKey(r.rooms), states: Object.fromEntries(r.states.map((x) => [x.roomId, x])),
    tasks: { ...s.tasks, ...byKey(r.tasks) }, serverTime: r.serverTime,
  }));
  for (const m of r.messages) upsertMessage(m, false);
  for (const e of r.events) addEvent(e);
}

/** A message from the server: in the chat, in its thread, and replacing the matching unsent copy. */
export function upsertMessage(m: Message, live = true) {
  set((s) => {
    const known = !!s.byId[m.id];
    const out: Partial<State> = { byId: { ...s.byId, [m.id]: m } };
    if (inChat(m) && s.msgs[m.roomId]) out.msgs = { ...s.msgs, [m.roomId]: insertSorted(s.msgs[m.roomId], m) };
    if (m.parentId && s.threads[m.parentId]) out.threads = { ...s.threads, [m.parentId]: insertSorted(s.threads[m.parentId], m) };
    if (m.clientId && m.userId === s.me?.id) out.pending = s.pending.filter((p) => p.clientId !== m.clientId);
    const st = s.states[m.roomId];
    if (st && inChat(m) && !known) {
      const mine = m.userId === s.me?.id;
      out.states = { ...s.states, [m.roomId]: {
        ...st, lastId: Math.max(st.lastId, m.id),
        lastReadId: mine ? Math.max(st.lastReadId, m.id) : st.lastReadId,
        unread: !mine && live && m.id > st.lastReadId && !m.deletedAt ? st.unread + 1 : st.unread,
        mentions: !mine && live && m.id > st.lastReadId && !m.deletedAt && m.mentions?.includes(s.me!.id) ? st.mentions + 1 : st.mentions,
      } };
    }
    return out;
  });
}

export function setRead(roomId: number, lastReadId: number) {
  set((s) => {
    const st = s.states[roomId];
    if (!st || st.lastReadId >= lastReadId) return {};
    const after = (s.msgs[roomId] ?? []).filter((m) => m.id > lastReadId && m.userId !== s.me?.id && !m.deletedAt);
    return { states: { ...s.states, [roomId]: { ...st, lastReadId, unread: s.msgs[roomId] ? after.length : 0, mentions: s.msgs[roomId] ? after.filter((m) => m.mentions?.includes(s.me!.id)).length : 0 } } };
  });
}

export function upsertRoom(r: Room) {
  set((s) => ({
    rooms: { ...s.rooms, [r.id]: r },
    states: s.states[r.id] ? s.states : { ...s.states, [r.id]: { roomId: r.id, lastReadId: 0, lastId: 0, unread: 0, mentions: 0, notify: "all", own: null } },
  }));
}
export function removeRoom(id: number) {
  set((s) => {
    const rooms = { ...s.rooms }; delete rooms[id];
    const msgs = { ...s.msgs }; delete msgs[id];
    return { rooms, msgs, route: s.route.v === "room" && s.route.id === id ? { v: "home" } : s.route };
  });
}

export function upsertTask(t: Task) { set((s) => ({ tasks: { ...s.tasks, [t.id]: t } })); }
export function removeTask(id: number) {
  set((s) => { const tasks = { ...s.tasks }; delete tasks[id]; return { tasks, route: s.route.v === "task" && s.route.id === id ? { v: "tasks", tab: "mine" } : s.route }; });
}
export function addEvent(e: TaskEvent) {
  set((s) => {
    const l = s.events[e.taskId];
    if (!l || l.some((x) => x.id === e.id)) return {};
    return { events: { ...s.events, [e.taskId]: [...l, e].sort((a, b) => a.id - b.id) } };
  });
}

// ---------- loading ----------

export async function loadRoom(id: number) {
  const msgs = await api.page(id);
  set((s) => {
    // Keep cached messages that are newer than the page (none normally), then the fresh page.
    const merged = msgs.reduce(insertSorted, (s.msgs[id] ?? []).filter((m) => m.id > (msgs.at(-1)?.id ?? 0)));
    return { msgs: { ...s.msgs, [id]: merged }, older: { ...s.older, [id]: msgs.length >= 60 }, byId: { ...s.byId, ...byKey(msgs) } };
  });
}
export async function loadOlder(id: number) {
  const first = get().msgs[id]?.[0];
  if (!first || !get().older[id]) return;
  const msgs = await api.page(id, first.id);
  set((s) => ({ msgs: { ...s.msgs, [id]: [...msgs, ...(s.msgs[id] ?? [])] }, older: { ...s.older, [id]: msgs.length >= 60 }, byId: { ...s.byId, ...byKey(msgs) } }));
}
export async function loadThread(root: number) {
  const t = await api.thread(root);
  set((s) => ({ threads: { ...s.threads, [root]: t.replies }, byId: { ...s.byId, [root]: t.root, ...byKey(t.replies) } }));
}
export async function loadTask(id: number) {
  const d = await api.task(id);
  set((s) => ({ tasks: { ...s.tasks, [id]: d.task }, events: { ...s.events, [id]: d.events }, sources: { ...s.sources, [id]: d.source } }));
}

// ---------- sending ----------

const cid = () => crypto.randomUUID();

export function setTyping(roomId: number, parentId: number | null, userId: number) {
  const key = `${roomId}:${parentId ?? 0}`;
  set((s) => ({ typing: { ...s.typing, [key]: { ...(s.typing[key] ?? {}), [userId]: Date.now() + 5000 } } }));
}
export function stopTyping(roomId: number, parentId: number | null, userId: number) {
  const key = `${roomId}:${parentId ?? 0}`;
  set((s) => { const t = { ...(s.typing[key] ?? {}) }; delete t[userId]; return { typing: { ...s.typing, [key]: t } }; });
}

export async function send(p: { roomId: number; body: string; parentId?: number; alsoInChat?: boolean; files?: FileRef[] }) {
  const pend: Pending = { clientId: cid(), roomId: p.roomId, body: p.body, parentId: p.parentId, alsoInChat: p.alsoInChat, files: p.files ?? [], createdAt: new Date().toISOString() };
  set((s) => ({ pending: [...s.pending, pend] }));
  await flush(pend);
}

async function flush(p: Pending) {
  try {
    const m = await api.send({ roomId: p.roomId, body: p.body, parentId: p.parentId, alsoInChat: p.alsoInChat, clientId: p.clientId, fileIds: p.files.map((f) => f.id) });
    upsertMessage(m);
  } catch (e: any) {
    const offline = !e.status;
    set((s) => ({ pending: s.pending.map((x) => (x.clientId === p.clientId ? { ...x, failed: offline ? "Waiting for connection" : e.message || "Not sent" } : x)) }));
  }
}

export function retry(clientId: string) {
  const p = get().pending.find((x) => x.clientId === clientId);
  if (!p) return;
  set((s) => ({ pending: s.pending.map((x) => (x.clientId === clientId ? { ...x, failed: undefined } : x)) }));
  return flush({ ...p, failed: undefined });
}
export function discard(clientId: string) { set((s) => ({ pending: s.pending.filter((x) => x.clientId !== clientId) })); }

/** After a reconnect, resend anything that was waiting. The client id makes this safe to repeat. */
export function resendWaiting() {
  for (const p of get().pending) if (p.failed === "Waiting for connection") retry(p.clientId);
}

export function go(route: Route, replace = false) {
  set({ route });
  const url = routeUrl(route);
  if (location.pathname + location.search !== url) (replace ? history.replaceState : history.pushState).call(history, null, "", url);
}
export function routeUrl(r: Route) {
  if (r.v === "room") return `/c/${r.id}${r.thread ? `/thread/${r.thread}` : ""}${r.focus ? `?m=${r.focus}` : ""}`;
  if (r.v === "search") return `/search?q=${encodeURIComponent(r.q)}`;
  if (r.v === "mentions" || r.v === "threads" || r.v === "later" || r.v === "unreads") return `/${r.v}`;
  if (r.v === "tasks") return r.tab === "mine" ? "/tasks/mine" : "/tasks";
  if (r.v === "task") return `/task/${r.id}`;
  return "/";
}
export function parseUrl(path: string, search = location.search): Route {
  let m;
  const q = new URLSearchParams(search);
  if ((m = path.match(/^\/c\/(\d+)(?:\/thread\/(\d+))?/))) return { v: "room", id: +m[1], thread: m[2] ? +m[2] : undefined, focus: q.get("m") ? +q.get("m")! : undefined };
  if (path === "/search") return { v: "search", q: q.get("q") ?? "" };
  if (path === "/mentions" || path === "/threads" || path === "/later" || path === "/unreads") return { v: path.slice(1) as "mentions" };
  if (path === "/tasks/mine") return { v: "tasks", tab: "mine" };
  if (path.startsWith("/tasks")) return { v: "tasks", tab: "everyone" };
  if ((m = path.match(/^\/task\/(\d+)/))) return { v: "task", id: +m[1] };
  return { v: "home" };
}
